import * as st from './heroshot.module.scss'
import * as React from 'react'
import { CirclePlayButton } from '../circlePlayButton/circlePlayButton'
import { ResponsiveImage } from '../images/responsiveImage'
import { SrcSize } from '../../utils/imageUtils'
import { connect } from 'react-redux'
import { IDetailPageState } from '../../reducers'
import { isRetailSelector } from '../../selectors/requestContext'
import { simpleSelectors } from '../../selectors/basicState'
import { isDeeplinkableActionReadySelector } from '../../selectors/pageContext'
import { imagesSelector } from '../../selectors/detailFragment'
import { playbackFirstChildSelector } from '../../selectors/actionFragment'
import { isTreatment } from '../../utils/utils'
import * as cn from 'classnames'
import { heroshotShouldBeShownSelector } from '../../selectors/component/atfComponentSelectors'
import { ConditionalWrapper } from '../../utils/conditionalWrapper'
import { ImageUrl } from '@utils'
import { isStarlightEnhancedSelector } from '../../selectors/shared'

const HEROSHOT_MOBILE_WIDTH = 360
const HEROSHOT_TABLET_WIDTH = 720
const HEROSHOT_DESKTOP_WIDTH = 1080

const STARLIGHT_MOBILE_WIDTH = 500
const STARLIGHT_PHABLET_WIDTH = 900
const STARLIGHT_TABLET_WIDTH = 1100
const STARLIGHT_DESKTOP_WIDTH = 1700



type StateProps = ReturnType<typeof mapStateToProps>
export type Props = StateProps

export const ConnectedHeroshot = ({ images, heroshotShouldBeShown, playbackData, isRetail,
                                    isDeeplinkableActionReady, atfTreatment, isStarlightEnhanced }: Props) => {
  if (!images) {
    return null
  }

  if (!heroshotShouldBeShown) { // adding blurred background for packshot
    const packshot = images.packshot

    if (!packshot) {
      return null
    }

    const background = new ImageUrl(packshot)
      .scaleWidth(300)
      .setBlur(80)
      .getImageUrl()

    const backgroundPackshotStyle = {
      backgroundImage: `url(${background})`
    }

    const blurredBackgroundProps = {
      alt: '', // Decorative image so no alt
      src: new ImageUrl(packshot).setBlur(90).getImageUrl(),
      baseValue: HEROSHOT_DESKTOP_WIDTH,
      srcSizes: [
        { 'max-phablet': HEROSHOT_MOBILE_WIDTH },
        { 'max-tablet': HEROSHOT_TABLET_WIDTH },
        {'min-tablet': HEROSHOT_DESKTOP_WIDTH}
      ] as SrcSize[]
    }

    return (
      <div className={cn(st.avHeroBackground, isTreatment(atfTreatment, 'T1', 'T2', 'T3') ? cn(st.avHeroBackgroundRedesign, st.packshotWrapper) : st.packshotBackground)}
           data-automation-id="hero-background" style={!isTreatment(atfTreatment,'T1', 'T2', 'T3') ? backgroundPackshotStyle : undefined}>
        <ConditionalWrapper condition={isTreatment(atfTreatment, 'T1', 'T2', 'T3')} wrapper={children => <div className={cn(st.imageFadeWrapper, st.imageFadeWrapperRedesign)}>{children}</div>}>
          {!isTreatment(atfTreatment, 'T1', 'T2', 'T3') && <div className={st.avHeroBackgroundFadeLeft}/>}
          {isTreatment(atfTreatment, 'T1', 'T2', 'T3') && <ResponsiveImage className={st.heroshot} classNameContainer={st.heroshot} {...blurredBackgroundProps} skipStripTags={true}/>}
          <div className={st.avHeroBackgroundFadeBottom}/>
        </ConditionalWrapper>
      </div>
    )
  }

  const heroshotImgProps = {
    alt: '', // Decorative image so no alt
    src: images.heroshot,
    baseValue: HEROSHOT_DESKTOP_WIDTH,
    srcSizes: [
      { 'max-phablet': HEROSHOT_MOBILE_WIDTH },
      { 'max-tablet': HEROSHOT_TABLET_WIDTH },
      { 'min-tablet': HEROSHOT_DESKTOP_WIDTH },
    ] as SrcSize[],
    blurUp: true,
    isAtfMainImage: true
  }

  const starlightImgProps = {
    alt: '', // Decorative image so no alt
    src: images.fullBackground16x9,
    baseValue: STARLIGHT_DESKTOP_WIDTH,
    srcSizes: [
      { 'max-phablet': STARLIGHT_MOBILE_WIDTH },
      { 'max-tablet': STARLIGHT_PHABLET_WIDTH },
      { 'min-tablet': STARLIGHT_TABLET_WIDTH },
      { 'min-tablet-landscape': STARLIGHT_DESKTOP_WIDTH },
    ] as SrcSize[],
    blurUp: true,
    isAtfMainImage: true
  }

  const useStarlightHero = Boolean(isStarlightEnhanced && images.fullBackground16x9)

  const shouldShowCirclePlay = !global.__NATIVEAPP__ && isRetail && !useStarlightHero

  return (
    <React.Fragment>
      <div className={cn(useStarlightHero ? st.fullscreenHero : st.avHeroBackground, st.heroshotOpacity, 'av-hero-background', {[st.avHeroBackgroundRedesign]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})}
           data-automation-id="hero-background">
        <div className={cn(st.imageFadeWrapper, {[st.imageFadeWrapperRedesign]: isTreatment(atfTreatment, 'T1', 'T2', 'T3')})}>
          <ResponsiveImage className={st.heroshot} classNameContainer={st.heroshot} {...(useStarlightHero ? starlightImgProps : heroshotImgProps)} />
          {!isTreatment(atfTreatment, 'T1', 'T2', 'T3') && <div className={useStarlightHero ? st.fullScreenFade : st.avHeroBackgroundFadeLeft}/>}
          <div className={st.avHeroBackgroundFadeBottom}/>
        </div>
        {!isTreatment(atfTreatment, 'T1', 'T2', 'T3') && shouldShowCirclePlay && (
          <div className="playback-large-circle-wrapper">
            <div id="dv-play-box-circle">
              {isDeeplinkableActionReady && playbackData && (
                <CirclePlayButton playbackData={playbackData}/>
              )}
            </div>
          </div>
        )}
      </div>
      {isTreatment(atfTreatment, 'T1', 'T2', 'T3') &&
        <div className={st.heroSpace}>
          {isTreatment(atfTreatment, 'T2', 'T3') && shouldShowCirclePlay && isDeeplinkableActionReady && playbackData &&
            <CirclePlayButton playbackData={playbackData} atfTreatment={atfTreatment}/>}
        </div>
      }
    </React.Fragment>
  )
}


const mapStateToProps = (state: IDetailPageState) => ({
  pageTitleId: simpleSelectors.pageTitleId(state),
  isRetail: isRetailSelector(state),
  isDeeplinkableActionReady: isDeeplinkableActionReadySelector(state),
  images: imagesSelector(state),
  heroshotShouldBeShown: heroshotShouldBeShownSelector(state),
  playbackData: playbackFirstChildSelector(state),
  atfTreatment: simpleSelectors.atfTreatment(state),
  isStarlightEnhanced: isStarlightEnhancedSelector(state)
})

export const Heroshot = connect(mapStateToProps)(ConnectedHeroshot)